import { selectFocus, selectModeData, selectProject } from "../../../redux/selectors/editor";
import { IGraphData, UpdateFocus, updateFocus, UpdateModeData, updateModeData } from "../../../redux/slices/editor";
import { connect } from "react-redux";
import { Box, Checkbox, Divider, FormControlLabel, IconButton, List, ListItem, ListItemText, Toolbar, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useCallback, useEffect, useMemo } from "react";
import { selectConstants, selectNetwork, selectTags, selectUser } from "../../../redux/selectors/firebase";
import { NetworkData } from "../../../fstn/network";
import { Constants, ITag, Tags } from "../StateTags";
import { getConstant, getVisibleGroupableTagStates } from "../../../utility/constantsTagsUtils";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import GroupableData from "../../dialogs/project/GroupableData";
import { getNodesInRoot } from "../../../utility/networkUtils";
import { PathwayUser } from "../../../redux/types/firebase";

const useStyles = makeStyles((theme) => ({
    toolbar: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.background.paper,
        marginTop: theme.spacing(1),
        minHeight: 0,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1)
    },
}));

interface IProps
{
    user: PathwayUser
    graphData: IGraphData
    constants: Constants
    tags: Tags
    network: NetworkData
    project: string
    focus: string
    updateModeData: UpdateModeData
    updateFocus: UpdateFocus
}
function GroupManager({ graphData, project, user, focus, updateFocus, updateModeData, tags, constants, network }: IProps)
{
    const classes = useStyles();

    const statesInRoot = useMemo(() => getNodesInRoot(graphData.root, network),
        [graphData.root, network]);

    const visibleGroups = useMemo(() => getVisibleGroupableTagStates(graphData.root, network, tags, constants),
        [graphData, network, tags, constants]);

    const setActiveGroup = (group: string) =>
    {
        updateModeData({ activeGroup: graphData.activeGroup === group ? "" : group });
    }

    const getGroupIndex = useCallback((id: string) =>
    {
        return Object.values(visibleGroups[graphData.activeGroup]).findIndex((value) => value.includes(id));
    }, [graphData.activeGroup, visibleGroups]);

    const focusGroupValue = useCallback((focusTag: ITag) =>
    {
        const currentFocusIndex = getGroupIndex(focus);
        const maxIndex = Object.values(visibleGroups[graphData.activeGroup]).length - 1;

        const state = Object.values(statesInRoot)
            .find((state) => state in tags && Object.values(tags[state])
                .find((tag) => tag.type === focusTag.type
                    && tag.value === focusTag.value
                    && (currentFocusIndex === maxIndex ? getGroupIndex(state) === 0 : getGroupIndex(state) > currentFocusIndex)));

        if (state)
        {
            if (graphData.root !== network.states[state].parent)
            {
                updateModeData({ root: network.states[state].parent })
            }
            updateFocus(state);
        }
    }, [statesInRoot, graphData.activeGroup, network.states, tags, graphData.root, visibleGroups, updateFocus, updateModeData, getGroupIndex, focus])

    const selectGroupOfValue = useCallback((selectTag: ITag) =>
    {
        const selected = Object.values(statesInRoot).filter((state) => state in tags && Object.values(tags[state])
            .find((tag) => tag.type === selectTag.type && tag.value === selectTag.value));
        if (selected.length > 0)
        {
            updateModeData({ selected, selectedActive: selected[0] });
        }
    }, [statesInRoot, tags, updateModeData]);

    const toggleVisible = useCallback((selectTag: ITag) =>
    {
        const key = String(selectTag.value);
        const hidden = graphData.hiddenGroupValues[key] === true;
        updateModeData({ hiddenGroupValues: { ...graphData.hiddenGroupValues, [key]: !hidden } });
    }, [graphData.hiddenGroupValues, updateModeData]);

    const toggleAllVisible = useCallback((values: any[], allVisible: boolean) =>
    {
        const hiddenGroupValues = values.reduce((mapObj, value) =>
        {
            mapObj[String(value)] = allVisible;
            return mapObj;
        }, {} as { [value: string]: boolean });

        updateModeData({ hiddenGroupValues });
    }, [updateModeData]);

    const getGroupData = (constraintName: string) =>
    {
        const constant = constants[constraintName];
        const constraintEntry = Object.entries(constant.constraints ?? {}).find(([id, constraint]) => constraint.type === "groupable");
        if (constraintEntry)
        {
            const [id, constraint] = constraintEntry;

            return <GroupableData constraint={constraint}
                stateFilter={statesInRoot}
                project={project}
                constant={constraintName}
                focusGroupValue={focusGroupValue}
                selectGroupOfValue={selectGroupOfValue}
                toggleVisible={toggleVisible}
                toggleAllVisible={toggleAllVisible}
                hiddenGroupMap={graphData.hiddenGroupValues}
                id={id} admin={user.roles.admin === true} />
        }
        return null;
    }

    const handleChangeSplit = (event: React.ChangeEvent<HTMLInputElement>) =>
    {
        updateModeData({ splitGroupsOnBranching: event.target.checked });
    };

    useEffect(() =>
    {
        updateModeData({ hiddenGroupValues: {} });
    }, [graphData.activeGroup, updateModeData]);

    return (
        <Box display="flex" flexDirection="column">
            <FormControlLabel control={<Checkbox checked={graphData.splitGroupsOnBranching} onChange={handleChangeSplit} />} label={"Split group on merge or branch"} />
            <Toolbar classes={{ dense: classes.toolbar }} disableGutters variant="dense">
                <Typography flexGrow={1} variant="h6">Groups</Typography>
            </Toolbar>
            <Divider />
            <List>
                {Object.keys(visibleGroups).map(key =>
                    <ListItem key={key}>
                        <Box display={"flex"} flexDirection={"column"}>
                            <Box display={"flex"} flexDirection={"row"}>
                                <IconButton onClick={() => setActiveGroup(key)}>
                                    {key === graphData.activeGroup ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                                <ListItemText primary={getConstant(key, constants)?.name} />
                            </Box>
                            <Box>
                                {key === graphData.activeGroup && getGroupData(key)}
                            </Box>
                        </Box>
                        <Divider />
                    </ListItem>
                )}
            </List>
        </Box>
    );
}
const mapStateToProps = (store: never) => ({
    user: selectUser(store),
    project: selectProject(store),
    graphData: selectModeData(store) as IGraphData,
    tags: selectTags(store) ?? {},
    constants: selectConstants(store) ?? {},
    network: selectNetwork(store),
    focus: selectFocus(store),
});

export default connect(mapStateToProps, {
    updateModeData,
    updateFocus
})(GroupManager);
